import { Component, Injector, Input, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { DynamicService } from 'src/app/services/dynamic.service';
import { token } from '../roles.component';
import { MessageService } from 'src/app/services/message.service';
import { MasterService } from 'src/app/services/master.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { COLLECTION_METHODS } from 'src/app/constants/payments';
import { FINANCING_OPTIONS } from 'src/app/constants/contact';
import * as languageLibrary from '../../../../../../services/language'
import { FinanceType } from 'src/app/models/finance-type.model';
import { Form } from 'src/app/models/form.model';
import { LienholderRole } from 'src/app/models/role-lienholder.model';
import { Subscription } from 'rxjs';
import { formatCurrency, formatPercent } from 'src/app/utils/format';

@Component({
    selector: '[role-lienholder]',
    templateUrl: './role-lienholder.component.html',
    styleUrls: ['./role-lienholder.component.scss'],
    standalone: false
})
export class RoleLienholderComponent implements OnInit, OnChanges, OnDestroy {
    
    /** */
    protected contactId: number = 0
    
    /** */
    protected contactRoleId: number = 0
    
    /** */
    protected dataIn: string;
    
    /** */
    public id: number = 0;
    
    /** */
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'
    
    /** */
    protected words = languageLibrary.language
    
    /** */
    public collectionMethods: string[] = COLLECTION_METHODS
    
    /** */
    public financingOptions: FinanceType[] = FINANCING_OPTIONS

    /** A list of sale contacts available in the system. */
    protected contracts: Form[] = []

    /** */
    public lienholderInformation = new FormGroup({
        id: new FormControl<number>(null),
        contactId: new FormControl<number>(null),
        defaultCollectionMethod: new FormControl<string | null>(null, [Validators.required]),
        stateFormId: new FormControl<number>(null, [Validators.required]),
        financesSaleType: new FormControl<number>(null, [Validators.required]),
        deductWarranty: new FormControl<boolean>(false, []),
        deductGap: new FormControl<boolean>(false, []),
        hideNameOnContract: new FormControl<boolean>(false, []),
        electronicTitle: new FormControl<boolean>(false, []),
        comprehensiveDeductible: new FormControl<string>(formatCurrency(0), []),
        collisionDeductible: new FormControl<string>(formatCurrency(0), []),
        certifiedId: new FormControl<string | null>(null, []),
        splitRate: new FormControl<string>(formatPercent(0), [])
    })

    private subscriptions: Subscription[] = []

    /** */
    public calledFrom: string;

    constructor(private master: MasterService, private ms: MessageService, private inject: Injector, private serv: DynamicService) {}

    ngOnInit() {
        this.dataIn = this.inject.get(token);
        this.contactId = this.dataIn['contactId']
        this.contactRoleId = this.dataIn['contactRoleId']
        this.calledFrom = this.dataIn['calledFrom']

        this.subscriptions.push(
            this.lienholderInformation.get('financesSaleType').valueChanges.subscribe((method) => {
                this.fetchContracts()
            })
        )

        this.getRoleLienholder()
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.contactId) {
            this.lienholderInformation.reset()
            this.lienholderInformation.get("contactId").setValue(this.contactId)
            this.getRoleLienholder()
        }
    }

    getRoleLienholder() {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }

        this.master.get(`contacts/${this.contactId}/lienholder`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (res.data.contactLienholders.length <= 0) {
                return
            }

            this.setInformation(res.data.contactLienholders[0])
            this.fetchContracts()

            if (res.data.contactLienholders.length == 0) {
                if (this.calledFrom == "contactAdvancedSearch") {
                    this.serv.outputFromDynamicComponent({ action: "setupNeeded", setupNeeded: true, type: "lienholder" });
                }

                return
            }

            if (this.calledFrom == "contactAdvancedSearch") {
                this.serv.outputFromDynamicComponent({ action: "setupNeeded", setupNeeded: false, type: "lienholder" });
                return
            }
        })
    }

    setInformation(lienholder: LienholderRole) {
        this.id = lienholder.id
        this.lienholderInformation.setValue({
            id: lienholder.id,
            contactId: this.contactId,
            stateFormId: lienholder.stateFormId,
            defaultCollectionMethod: lienholder.defaultCollectionMethod,
            financesSaleType: lienholder.financesSaleType,
            deductWarranty: lienholder.deductWarranty,
            deductGap: lienholder.deductGap,
            hideNameOnContract: lienholder.hideNameOnContract,
            electronicTitle: lienholder.electronicTitle,
            comprehensiveDeductible: formatCurrency(lienholder.comprehensiveDeductible),
            collisionDeductible: formatCurrency(lienholder.collisionDeductible),
            splitRate: formatPercent(lienholder.splitRate || 0),
            certifiedId: lienholder.certifiedId
        }, {emitEvent: false})
    }

    /**
     * Get a list of sale contracts in the system.
     */
    fetchContracts() {
        const financeType = this.lienholderInformation.get('financesSaleType').value
        
        let financeTypeParam = ''
        if (financeType && (financeType > 0)) {
            const type: FinanceType = this.financingOptions.find((option) => option.id == financeType)
            financeTypeParam = `&financeType=${encodeURIComponent(type.name.toLocaleLowerCase())}`
        }

        this.contracts = []
        this.master.get(`forms?module=sales&panel=print-center&category=finance&family=contract${financeTypeParam}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (!res.data.forms || (res.data.forms.length <= 0)) {
                return
            }

            this.contracts = res.data.forms
        })
    }


    public save() {
        if (!this.lienholderInformation.valid) {
            return
        }

        if (this.id <= 0) {
            this.create()
            return
        }

        this.modify()
    }

    create() {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.lienholderInformation.value))
        payload.financesSaleType = parseInt(payload.financesSaleType)
        payload.stateFormId = parseInt(payload.stateFormId)
        delete(payload.id)
        delete(payload.contactId)

        this.master.post(`contacts/${this.contactId}/lienholder`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.contactLienholder)

            if (this.calledFrom == "contactAdvancedSearch") {
                this.serv.outputFromDynamicComponent({ action: "setupSaved", setupSaved: true, type: "lienholder" });
            }

        })
    }

    modify() {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }

        if (!this.id || (this.id <= 0)) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.lienholderInformation.value))
        payload.financesSaleType = parseInt(payload.financesSaleType)
        payload.stateFormId = parseInt(payload.stateFormId)
        delete(payload.id)
        delete(payload.contactId)

        this.master.put(`contacts/${this.contactId}/lienholder/${this.id}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (this.calledFrom == "contactAdvancedSearch") {
                this.serv.outputFromDynamicComponent({ action: "setupSaved", setupSaved: true, type: "lienholder" });
                this.ms.channelComponents$.emit({ message: 'lienholderChanged', section: 'contacts', value: payload})
            }
        })
    }

    deactivateRole() {
        this.serv.outputFromDynamicComponent({ action: "deactivate", contactRoleId: this.contactRoleId });
    }

}
